  import { createContext, useContext, useState, useEffect, ReactNode } from "react";
  import API from "../api/api";
  import { UserContext } from "./UserContext";

  // ================== TYPES ==================
  export interface IInvitacion {
    id: number;
    nombre: string;
    receptor_codigo: string;
    grupo: string;
    estado: "pendiente" | "aceptada" | "rechazada" | null;
  }

  interface IGrupoData {
    grupo: { id: number; nombre?: string; codigo?: string };
    miembros: { id: number; nombre: string; codigo_usuario: string }[];
  }

  interface RankingAmigo {
  id: number;
  nombre: string;
  puntos: number;
  posicion: number;
}

export interface IGrupoContext {
  loading: boolean;
  error: string | null;

  invitacionesEnviadas: IInvitacion[];
  invitacionesRecibidas: IInvitacion[];
  miGrupo: IGrupoData | null;

  usuariosEncontrados: { id: number; nombre: string; codigo_usuario: string }[];

 rankingAmigos: {
  ranking: RankingAmigo[];
  yo: RankingAmigo | null;
};


  limpiarUsuariosEncontrados: () => void;

  buscarUsuario: (query: string) => Promise<void>;
  enviarInvitacion: (idReceptor: number) => Promise<void>;
  cancelarInvitacion: (idInv: number) => Promise<void>;
  responderInvitacion: (
    idInv: number,
    resp: "aceptada" | "rechazada"
  ) => Promise<void>;

  recargarInvitaciones: () => Promise<void>;

  cargarRankingAmigos: () => Promise<void>;  // 👈 AÑADIDO
}


  // ================== CONTEXT ==================
  export const GrupoContext = createContext<IGrupoContext | null>(null);

  export const GrupoProvider = ({ children }: { children: ReactNode }) => {
    const { user } = useContext(UserContext) ?? { user: null };

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rankingAmigos, setRankingAmigos] = useState<{
        ranking: RankingAmigo[];
        yo: RankingAmigo | null;
      }>({
        ranking: [],
        yo: null
      });
    const [invitacionesEnviadas, setInvEnviadas] = useState<IInvitacion[]>([]);
    const [invitacionesRecibidas, setInvRecibidas] = useState<IInvitacion[]>([]);
    const [miGrupo, setMiGrupo] = useState<IGrupoData | null>(null);
    const [usuariosEncontrados, setUsuariosEncontrados] = useState<
      { id: number; nombre: string; codigo_usuario: string }[]
    >([]);

    const start = () => {
      setLoading(true);
      setError(null);
    };
    const end = () => setLoading(false);

    // ================== LOADERS ==================
    const recargarInvitaciones = async () => {
      if (!user) return;
      try {
        const [env, rec] = await Promise.all([
          API.get(`/grupos/invitaciones/enviadas/${user.id}`),
          API.get(`/grupos/invitaciones/${user.id}`),
        ]);
        setInvEnviadas(env.data || []);
        setInvRecibidas(rec.data || []);
      } catch {
        setError("Error al cargar invitaciones");
      }
    };

    const recargarMiGrupo = async () => {
      if (!user) return;
      try {
        const res = await API.get(`/grupos/mi-grupo/${user.id}`);
        setMiGrupo(res.data || null);
      } catch {
        setError("Error al cargar grupo");
      }
    };

  const cargarRankingAmigos = async () => {
        if (!user) return;
        try {
          const res = await API.get(`/grupos/ranking/${user.id}`);
          setRankingAmigos(res.data);
        } catch {
          setError("Error al cargar ranking de amigos");
        }
      };



    useEffect(() => {
      if (!user) return;
      (async () => {
        start();
        await Promise.all([recargarMiGrupo(), recargarInvitaciones(), cargarRankingAmigos()]);
        end();
      })();
    }, [user?.id]);

    // ================== ACTIONS ==================
    const limpiarUsuariosEncontrados = () => {
      setUsuariosEncontrados([]);
    };

    const buscarUsuario = async (query: string) => {
      if (!query.trim()) {
        setUsuariosEncontrados([]);
        return;
      }
      start();
      try {
        const res = await API.get(`/grupos/usuarios/buscar/${query}`);
        setUsuariosEncontrados(res.data || []);
      } catch {
        setError("Error al buscar usuario");
      } finally {
        end();
      }
    };

    const enviarInvitacion = async (idReceptor: number) => {
      if (!user) return;
      start();
      try {
        await API.post("/grupos/invitar", {        
          id_remitente: user.id,
          id_receptor: idReceptor,
        });
        await recargarInvitaciones();
      } catch {
        setError("No se pudo enviar invitación");
      } finally {
        end();
      }
    };

    const cancelarInvitacion = async (idInv: number) => {
      if (!user) return;
      start();
      try {
        await API.delete(`/grupos/invitaciones/${idInv}`);
        await recargarInvitaciones();
      } catch {
        setError("No se pudo cancelar invitación");
      } finally {
        end();
      }
    };

    const responderInvitacion = async (idInv: number, resp: "aceptada" | "rechazada") => {
      if (!user) return;
      start();
      try {
        await API.post("/grupos/invitacion/responder", {
          invitacionId: idInv,
          respuesta: resp,
        });
        await recargarInvitaciones();
        await recargarMiGrupo();
      } catch {
        setError("No se pudo responder invitación");
      } finally {
        end();
      }
    };

    return (
  <GrupoContext.Provider
    value={{
      loading,
      error,
      invitacionesEnviadas,
      invitacionesRecibidas,
      miGrupo,
      usuariosEncontrados,

      limpiarUsuariosEncontrados,
      buscarUsuario,
      enviarInvitacion,
      cancelarInvitacion,
      responderInvitacion,
      recargarInvitaciones,
      
      rankingAmigos,
      cargarRankingAmigos,
    }}
  >
    {children}
  </GrupoContext.Provider>
);

  };
